/**
 * Module Product Tabs
 * Gestion des onglets avec position absolue et animation fluide de hauteur
 */
import { gsap } from 'gsap';

class ProductTabs {
    constructor() {
        this.tabButtons = document.querySelectorAll('[data-tab]');
        this.tabContents = document.querySelectorAll('[data-tab-content]');
        this.tabsContainer = document.querySelector('.tabs-content');
        this.tabIndicator = document.querySelector('.tab-indicator');
        this.isAnimating = false; // Protection contre les clics rapides
        
        this.init();
    }

    init() {
        // Initialiser les onglets
        this.initTabs();
        
        // Initialiser l'indicateur mobile
        this.initTabIndicator();
        
        // Préparer le layout avec position absolue
        this.setupAbsoluteLayout();
    }

    setupAbsoluteLayout() {
        if (!this.tabsContainer) return;

        // Trouver l'onglet actif initial
        const activeContent = document.querySelector('[data-tab-content="description"]');
        
        if (activeContent) {
            // Rendre visible temporairement pour mesurer
            gsap.set(activeContent, { 
                visibility: 'visible',
                opacity: 1,
                position: 'relative'
            });
            
            // Mesurer et définir la hauteur initiale
            const initialHeight = activeContent.offsetHeight;
            gsap.set(this.tabsContainer, { height: initialHeight });
            
            // Remettre en position absolue
            gsap.set(activeContent, { 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%'
            });
        }

        // Préparer tous les autres contenus
        this.tabContents.forEach(content => {
            if (content !== activeContent) {
                gsap.set(content, { 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    opacity: 0,
                    visibility: 'hidden'
                });
            }
        });
    }

    initTabs() {
        if (this.tabButtons.length === 0) return;

        this.tabButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                
                const targetTab = button.getAttribute('data-tab');
                this.switchTab(targetTab, button);
            });
        });
    }

    initTabIndicator() {
        if (!this.tabIndicator) return;

        // Positionner l'indicateur sur le premier onglet actif au chargement
        const activeButton = document.querySelector('[data-tab][aria-selected="true"]');
        if (activeButton) {
            this.moveIndicatorToTab(activeButton, false); // Pas d'animation au chargement
        }

        // Gérer le redimensionnement de la fenêtre
        window.addEventListener('resize', () => {
            const currentActiveButton = document.querySelector('[data-tab][aria-selected="true"]');
            if (currentActiveButton) {
                this.moveIndicatorToTab(currentActiveButton, false);
            }
        });
    }

    moveIndicatorToTab(targetButton, animate = true) {
        if (!this.tabIndicator || !targetButton) return;

        // Trouver l'index du bouton dans la liste des boutons visibles
        const visibleButtons = Array.from(this.tabButtons).filter(btn => 
            btn.offsetParent !== null // Vérifie que le bouton est visible
        );
        const buttonIndex = visibleButtons.indexOf(targetButton);
        
        if (buttonIndex === -1) return;

        // Calculer la largeur et position basées sur l'index
        const nav = targetButton.closest('nav');
        const navStyles = getComputedStyle(nav);
        const navPadding = parseInt(navStyles.paddingLeft) + parseInt(navStyles.paddingRight);
        const availableWidth = nav.offsetWidth - navPadding;
        const buttonWidth = availableWidth / visibleButtons.length;
        const left = buttonIndex * buttonWidth;
        const height = targetButton.offsetHeight;

        if (animate) {
            // Animation fluide avec GSAP
            gsap.to(this.tabIndicator, {
                x: left,
                width: buttonWidth,
                height: height,
                duration: 0.3,
                ease: "power2.out"
            });
        } else {
            // Positionnement initial sans animation
            gsap.set(this.tabIndicator, {
                x: left,
                width: buttonWidth,
                height: height
            });
        }
    }

    switchTab(targetTab, activeButton) {
        // Empêcher les clics pendant l'animation
        if (this.isAnimating) return;
        
        // Vérifier si c'est déjà l'onglet actif
        if (activeButton.getAttribute('aria-selected') === 'true') return;

        // Marquer comme en cours d'animation
        this.isAnimating = true;

        // Animer l'indicateur vers le nouvel onglet
        this.moveIndicatorToTab(activeButton, true);

        // Désactiver tous les onglets
        this.tabButtons.forEach(btn => {
            btn.classList.remove('text-white', 'tab-selected', 'hover:text-white');
            btn.classList.add('text-gray-900', 'hover:text-orange-500', 'cursor-pointer');
            btn.setAttribute('aria-selected', 'false');
        });

        // Activer l'onglet sélectionné
        activeButton.classList.remove('text-gray-900', 'hover:text-orange-500', 'cursor-pointer');
        activeButton.classList.add('text-white', 'tab-selected', 'hover:text-white');
        activeButton.setAttribute('aria-selected', 'true');

        // Animer la transition
        const targetContent = document.querySelector(`[data-tab-content="${targetTab}"]`);
        if (targetContent) {
            this.animateToTab(targetContent);
        } else {
            // Si pas de contenu trouvé, débloquer l'animation
            this.isAnimating = false;
        }
    }

    animateToTab(newContent) {
        // Trouver le contenu actuellement visible
        const currentContent = Array.from(this.tabContents).find(content => 
            gsap.getProperty(content, "opacity") === 1
        );

        if (currentContent === newContent) {
            this.isAnimating = false;
            return;
        }

        // S'assurer qu'aucun autre contenu n'est visible
        this.tabContents.forEach(content => {
            if (content !== currentContent && content !== newContent) {
                gsap.set(content, { 
                    opacity: 0, 
                    visibility: 'hidden',
                    zIndex: 0
                });
            }
        });

        // Mesurer la hauteur du nouveau contenu
        gsap.set(newContent, { 
            visibility: 'visible',
            opacity: 0,
            position: 'relative'
        });
        const newHeight = newContent.offsetHeight;
        
        // Remettre en position absolue
        gsap.set(newContent, { 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%'
        });

        // Timeline d'animation
        const tl = gsap.timeline({
            onComplete: () => {
                // Débloquer l'animation à la fin
                this.isAnimating = false;
                // Déclencher un événement pour notifier le changement de hauteur
                this.notifyHeightChange();
            }
        });

        // Animer la hauteur du conteneur ET notifier en temps réel
        tl.to(this.tabsContainer, {
            height: newHeight,
            duration: 0.5,
            ease: "power2.inOut",
            onUpdate: () => {
                // Déclencher l'événement pendant l'animation pour synchroniser la galerie
                this.notifyHeightChangeRealtime();
            }
        });

        // Fade out l'ancien contenu si existe
        if (currentContent) {
            tl.to(currentContent, {
                opacity: 0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    gsap.set(currentContent, { 
                        visibility: 'hidden',
                        zIndex: 0
                    });
                }
            }, 0);
        }

        // Fade in le nouveau contenu
        tl.set(newContent, { 
            visibility: 'visible',
            zIndex: 10
        }, 0)
        .to(newContent, {
            opacity: 1,
            duration: 0.4,
            ease: "power2.out"
        }, "-=0.2");

        // Mettre à jour les attributs ARIA
        this.tabContents.forEach(content => {
            content.setAttribute('aria-hidden', content === newContent ? 'false' : 'true');
        });
    }

    notifyHeightChange() {
        // Déclencher un événement personnalisé pour notifier les autres modules
        const event = new CustomEvent('tabsHeightChange', {
            detail: {
                container: this.tabsContainer,
                newHeight: this.tabsContainer.offsetHeight
            }
        });
        document.dispatchEvent(event);
    }

    notifyHeightChangeRealtime() {
        // Déclencher un événement en temps réel pendant l'animation
        const event = new CustomEvent('tabsHeightChangeRealtime', {
            detail: {
                container: this.tabsContainer,
                currentHeight: this.tabsContainer.offsetHeight
            }
        });
        document.dispatchEvent(event);
    }

    updateContainerHeight() {
        // Trouver le contenu actuellement visible
        const activeContent = Array.from(this.tabContents).find(content => 
            gsap.getProperty(content, "opacity") === 1
        );

        if (activeContent) {
            // Mesurer la nouvelle hauteur
            gsap.set(activeContent, { position: 'relative' });
            const newHeight = activeContent.offsetHeight;
            gsap.set(activeContent, { position: 'absolute' });

            // Animer vers la nouvelle hauteur
            gsap.to(this.tabsContainer, {
                height: newHeight,
                duration: 0.4,
                ease: "power2.out"
            });
        }
    }
}

// Export pour utilisation dans scripts.js
export default ProductTabs;
